import React from 'react'
import { Icon } from '@iconify/react'
import {
  Typography,
  withStyles,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
} from '@material-ui/core'
import styles from '../../styles'
import { useDispatch, useSelector } from 'react-redux'
import { patchPriceAction } from 'src/reducers/collection/action'
import { CoreButton, CoreTextField } from 'src/components/core'
import { currencyList } from 'src/model/payment'
import { BackButton } from '..'
import { niceWork } from './svg'
import { formatPrice } from 'src/components/editor/components/custom-plugins/simple-box-wrapper-options/utils'
import { track } from '@amplitude/analytics-browser'

const getBillingInterval = billingPeriod => {
  const intervalMap = {
    oneTime: null,
    week: 'week',
    month: 'month',
    every3Month: 'month',
    every6Months: 'month',
    year: 'year',
  }
  return intervalMap[billingPeriod]
}

const getIntervalCount = billingPeriod => {
  const countMap = {
    every3Month: 3,
    every6Months: 6,
  }
  return countMap[billingPeriod] || 1
}

const getPriceType = billingPeriod => {
  return billingPeriod === 'oneTime' ? 1 : 2
}

const getBillingPeriodFromPrice = price => {
  if (!price) return ''

  if (price.priceType === 1) {
    return 'oneTime'
  }

  // Only treat priceType === 2 as recurring. A null/undefined priceType means
  // "nothing saved yet" and should leave the caller's default in place rather
  // than forcing 'month'.
  if (price.priceType === 2) {
    if (price.priceMetadata?.recurring) {
      const { interval, interval_count } = price.priceMetadata.recurring

      if (interval === 'week') return 'week'
      if (interval === 'month') {
        if (interval_count === 1) return 'month'
        if (interval_count === 3) return 'every3Month'
        if (interval_count === 6) return 'every6Months'
      }
      if (interval === 'year') return 'year'
    } else {
      return 'month'
    }
  }

  return ''
}

const StepThree = props => {
  const {
    entity,
    collectionId,
    videoUrl,
    handleNext,
    onClickDone,
    handleBack,
    classes,
    paymentProvider,
    markStepCompleted,
    settings: settingsProp,
    onPatchPrice,
    paymentSettingType,
  } = props
  const dispatch = useDispatch()
  const [amount, setAmount] = React.useState('')
  const [currency, setCurrency] = React.useState('USD')
  const [billingPeriod, setBillingPeriod] = React.useState('oneTime')
  const [isStepCompleted, setIsStepCompleted] = React.useState(false)
  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const settings = settingsProp ?? collectionSettings

  React.useEffect(() => {
    const currentPrice = settings?.price

    if (currentPrice) {
      if (currentPrice.amount) {
        setAmount(currentPrice.amount.toString())
      }

      if (currentPrice.currency) {
        setCurrency(currentPrice.currency)
      }

      const period = getBillingPeriodFromPrice(currentPrice)
      if (period) {
        setBillingPeriod(period)
      }
    }
  }, [settings?.price])

  const selectedCurrency = currencyList.find(curr => curr.code === currency)
  const currencySymbol = selectedCurrency ? selectedCurrency.symbol : '$'

  const validateAmount = text => {
    const numberPattern = /^\d*\.?\d*$/
    return numberPattern.test(text) && text !== '' && parseFloat(text) > 0
  }

  const isValidAmount = validateAmount(amount)

  const getPriceId = billingPeriod => {
    const providerMap = {
      oneTime: 1,
      week: 2,
      month: 2,
      every3Month: 2,
      every6Months: 2,
      year: 2,
    }
    return providerMap[billingPeriod]
  }

  const dispatchPrice = (id, payload, callback) => {
    if (onPatchPrice) {
      onPatchPrice(id, payload, callback)
    } else {
      dispatch(patchPriceAction(id, payload, callback))
    }
  }

  const onClick = event => {
    if (!isStepCompleted) {
      setIsStepCompleted(true)
      markStepCompleted()
      onClickDone()
      handleSaveFromSuccess()

      // The DOM update below is only relevant for the collection-paywall flow whose price renders
      // on the landing page; certificate pricing has no such surface.
      if (paymentSettingType !== 'certificate'
          && collectionId
          && collectionSettings?.accessRule === 4) {
        const priceId = getPriceId(billingPeriod)
        const interval = getBillingInterval(billingPeriod)
        const intervalCount = getIntervalCount(billingPeriod)

        let paymentProviderType
        if (paymentProvider === 'stripe') {
          paymentProviderType = 1
        } else if (paymentProvider === 'lemon_squeeze') {
          paymentProviderType = 3
        } else {
          paymentProviderType = 2
        }

        const priceData = {
          priceType: priceId,
          amount: parseFloat(amount),
          currency: currency,
          paymentProvider: paymentProviderType,
          priceMetadata:
            billingPeriod !== 'oneTime'
              ? {
                  recurring: {
                    interval: interval,
                    interval_count: intervalCount,
                  },
                }
              : {},
        }

        const collectionElement = document.querySelector(
          `[data-collection-id="${collectionId}"]`,
        )
        if (collectionElement) {
          const priceElement = collectionElement.querySelector(
            '.section-price p',
          )
          if (priceElement) {
            priceElement.textContent = formatPrice(priceData)
          }
        }
      }

      return
    }

    const priceId = getPriceId(billingPeriod)
    const interval = getBillingInterval(billingPeriod)
    const intervalCount = getIntervalCount(billingPeriod)

    let paymentProviderType
    if (paymentProvider === 'stripe') {
      paymentProviderType = 1
    } else if (paymentProvider === 'lemon_squeeze') {
      paymentProviderType = 3
    } else {
      paymentProviderType = 2
    }

    const priceData = {
      priceType: priceId,
      amount: parseFloat(amount),
      currency: currency,
      paymentProvider: paymentProviderType,
      priceMetadata:
        billingPeriod !== 'oneTime'
          ? {
              recurring: {
                interval: interval,
                interval_count: intervalCount,
              },
            }
          : {},
    }
    track('Display_Price_Set', {
      amount: amount,
      currency: currency,
      period: billingPeriod,
    })

    dispatchPrice(collectionId ? collectionId : entity.id, priceData)

    if (
      paymentSettingType !== 'certificate'
      && collectionId
      && collectionSettings?.accessRule === 4
    ) {
      const collectionElement = document.querySelector(
        `[data-collection-id="${collectionId}"]`,
      )
      if (collectionElement) {
        const priceElement = collectionElement.querySelector('.section-price p')
        if (priceElement) {
          priceElement.textContent = formatPrice(priceData)
        }
      }
    }
  }

  const onChangeAmount = event => {
    setAmount(event.target.value)
  }

  const handleSaveFromSuccess = () => {
    const priceId = getPriceId(billingPeriod)
    const interval = getBillingInterval(billingPeriod)
    const intervalCount = getIntervalCount(billingPeriod)

    let paymentProviderType
    if (paymentProvider === 'stripe') {
      paymentProviderType = 1
    } else if (paymentProvider === 'lemon_squeeze') {
      paymentProviderType = 3
    } else {
      paymentProviderType = 2
    }

    const priceDataToSend = {
      priceType: priceId,
      amount: parseFloat(amount),
      currency: currency,
      paymentProvider: paymentProviderType,
      priceMetadata:
        billingPeriod !== 'oneTime'
          ? {
              recurring: {
                interval: interval,
                interval_count: intervalCount,
              },
            }
          : {},
    }

    dispatchPrice(
      collectionId ? collectionId : entity.id,
      priceDataToSend,
      () => {
        setTimeout(() => {
          setIsStepCompleted(false)
        }, 10000)
      },
    )
  }

  return (
    <div className={classes.stepZero_main2}>
      <div className={classes.fieldsContainer}>
        <div className={classes.fieldItem}>
          <Typography fontSize={'14px'} fontWeight={500} marginBottom={'8px'}>
            Amount to charge
          </Typography>
          <CoreTextField
            marginTop={false}
            label=""
            variant="outlined"
            value={amount}
            error={!isValidAmount && amount !== ''}
            helperText={
              !isValidAmount && amount !== ''
                ? 'Please enter a valid amount'
                : ' '
            }
            fullWidth
            onChange={onChangeAmount}
            margin="none"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            }}
            FormHelperTextProps={{
              style: {
                margin: 0,
                height: 0,
                visibility:
                  isValidAmount || amount === '' ? 'hidden' : 'visible',
              },
            }}
          />
        </div>

        <div className={classes.fieldItem}>
          <Typography fontSize={'14px'} fontWeight={500} marginBottom={'8px'}>
            Currency
          </Typography>
          <FormControl variant="outlined" fullWidth>
            <Select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              style={{ backgroundColor: '#fff' }}
            >
              {currencyList.map(curr => (
                <MenuItem key={curr.code} value={curr.code}>
                  {curr.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={classes.fieldItem}>
          <FormControl variant="outlined" fullWidth>
            <Typography fontSize={'14px'} fontWeight={500} marginBottom={'8px'}>
              Billing period
            </Typography>
            <Select
              value={billingPeriod}
              onChange={e => setBillingPeriod(e.target.value)}
              displayEmpty
              style={{ backgroundColor: '#fff' }}
            >
              <MenuItem value="" disabled>
                Select billing period
              </MenuItem>
              <MenuItem value="oneTime">One-time</MenuItem>
              <MenuItem value="week">Weekly</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
              <MenuItem value="every3Month">Every 3 months</MenuItem>
              <MenuItem value="every6Months">Every 6 months</MenuItem>
              <MenuItem value="year">Yearly</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      <div className={classes.infoDiv}>
        <Icon
          icon="material-symbols:info-rounded"
          style={{
            width: '20px',
            height: '20px',
            color: 'var(--mcg-gray, #919EAB)',
          }}
        />{' '}
        <Typography className={classes.textPrice}>
          This price is for display only and will appear on your landing page.
          It does not affect your actual payment setup.
        </Typography>
      </div>

      <div className={classes.stepTwo_main_buttons}>
        <BackButton handleBack={handleBack} />
        <CoreButton
          variant="contained"
          color="success"
          onClick={onClick}
          disabled={!isValidAmount || billingPeriod === ''}
        >
          Save
        </CoreButton>
      </div>
      {isStepCompleted && (
        <div className={classes.stepZero_main}>
          <div
            className={classes.stepZero_main_title}
            dangerouslySetInnerHTML={{ __html: niceWork }}
          />

          <div style={{ textAlign: 'center' }}>
            <Typography
              fontSize={'18px'}
              fontWeight={500}
              marginBottom={'16px'}
            >
              Nice work!
            </Typography>
            <Typography fontSize={'14px'}>
              You're all set to start earning from your content.
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(StepThree)
